import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import ArrowDownIcon from '@atlaskit/icon/glyph/arrow-down';
import Lozenge from '@atlaskit/lozenge';

import {
  Account,
  Allowance,
  BuildConfiguration,
  EditorEnum,
  OfficialTemplate,
  Repository,
} from 'src/components/pipelines/models';
import { getTemplates } from 'src/components/pipelines/utils/getTemplates';

import { INITIALLY_VISIBLE_TEMPLATES } from '../const';
import { OnboardingType } from '../types';
import customButtonTheme from '../utils/customButtomTheme';
import { EditorTypeToAnalyticSourceMap } from '../utils/util';

import ExistingConfigSection from './ExistingConfigSection';
import Header from './Header';
import { OnboardingContextProvider } from './OnboardingContext';
import PipelineCard from './PipelineCard';
import PipesCard from './PipesCard';
import QuotaBanner from './QuotaBanner';
import {
  ArrowWrapper,
  ChooseTemplatesMessage,
  CreatePipelineAction,
  RecommendedTemplate,
  ScrollWrapper,
  templateButtonTheme,
  TemplateCard,
  TemplateList,
  TemplateLogo,
  TemplatesWrapper,
  ValuePropContainer,
} from './styled';
import TwoStepVerification from './TwoStepVerification';

type Props = {
  account: Account;
  allowance: Allowance;
  repository: Repository;
  buildConfiguration: BuildConfiguration;
  openEditor: (yml: string) => void;
  enablePipelines: () => void;
};

const Onboarding = ({
  account,
  allowance,
  repository,
  buildConfiguration,
  openEditor,
  enablePipelines,
}: Props) => {
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const templatesElRef = useRef<HTMLDivElement>(null);

  const analyticsSource =
    EditorTypeToAnalyticSourceMap[EditorEnum.PipelinesOnboarding];
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [templates, setTemplates] = useState<OfficialTemplate[]>([]);

  const recommendedTemplate = templates[0];
  const remainingTemplates = templates.slice(
    1,
    showAllTemplates ? templates.length : INITIALLY_VISIBLE_TEMPLATES + 1
  );

  useEffect(() => {
    getTemplates<OfficialTemplate>().then(
      (allTemplates: OfficialTemplate[]) => {
        setTemplates(
          allTemplates
            .map(
              (officialTemplate, id) =>
                new OfficialTemplate({ ...officialTemplate, id })
            )
            .sort((a, b) => a.weight - b.weight)
        );
      }
    );
  }, [analyticsSource]);

  const onSelectTemplate = useCallback(
    (officialTemplate: OfficialTemplate) => {
      createAnalyticsEvent({
        action: 'clicked',
        actionSubject: 'tab',
        actionSubjectId: 'languageTemplateTab',
        source: analyticsSource,
        attributes: { tab: officialTemplate.language },
      }).fire();
      openEditor(officialTemplate.yml);
    },
    [analyticsSource, createAnalyticsEvent, openEditor]
  );

  const scrollIntoView = () => {
    if (templatesElRef.current) {
      templatesElRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const renderTemplateCard = (
    officialTemplate: OfficialTemplate,
    isRecommendedTemplate: boolean
  ) => {
    return (
      <TemplateCard
        key={`template_card_${officialTemplate.id}`}
        isRecommendedTemplate={templates[0] === officialTemplate}
      >
        <header>
          <TemplateLogo
            style={{ backgroundImage: `url(${officialTemplate.logo})` }}
          />
          <CustomThemeButton
            appearance="subtle"
            onClick={() => onSelectTemplate(officialTemplate)}
            theme={customButtonTheme(
              templateButtonTheme(isRecommendedTemplate)
            )}
          >
            Select
          </CustomThemeButton>
        </header>
        <article>
          <h4>{officialTemplate.name}</h4>
          <p>{officialTemplate.description}</p>
        </article>
      </TemplateCard>
    );
  };

  if (!allowance.isTrusted && !account.userHas2FaEnabled) {
    return (
      <OnboardingContextProvider analyticsSource={analyticsSource}>
        <TwoStepVerification onboardingType={OnboardingType.PIPELINES} />
      </OnboardingContextProvider>
    );
  }

  return (
    <OnboardingContextProvider analyticsSource={analyticsSource}>
      <ScrollWrapper>
        <QuotaBanner allowance={allowance} repository={repository} />
        <ValuePropContainer>
          <Header />
          {!buildConfiguration.hasYmlFile ? (
            <>
              <CreatePipelineAction onClick={scrollIntoView}>
                <CustomThemeButton appearance="link">
                  <p>Create your first pipeline</p>
                </CustomThemeButton>
                <ArrowWrapper>
                  <ArrowDownIcon label="scroll down" />
                </ArrowWrapper>
              </CreatePipelineAction>
              <PipelineCard />
              <PipesCard />
            </>
          ) : (
            <ExistingConfigSection
              repository={repository}
              enablePipelines={enablePipelines}
            />
          )}
        </ValuePropContainer>
        {!buildConfiguration.hasYmlFile && templates.length ? (
          <TemplatesWrapper ref={templatesElRef}>
            <RecommendedTemplate ref={templatesElRef}>
              <h3>Create your first pipeline</h3>
              <p>
                Get started with a template{' '}
                <Lozenge appearance="new">Recommended</Lozenge>
              </p>
              <TemplateList>
                {renderTemplateCard(recommendedTemplate, true)}
              </TemplateList>
            </RecommendedTemplate>
            <ChooseTemplatesMessage>
              <p>
                Or choose a template to build and deploy to a cloud service of
                your choice
              </p>
              <CustomThemeButton
                spacing="none"
                appearance="link"
                onClick={() => setShowAllTemplates(!showAllTemplates)}
              >
                {!showAllTemplates ? 'View more' : 'View less'}
              </CustomThemeButton>
            </ChooseTemplatesMessage>
            <TemplateList>
              {remainingTemplates.map(
                template => template && renderTemplateCard(template, false)
              )}
            </TemplateList>
          </TemplatesWrapper>
        ) : null}
      </ScrollWrapper>
    </OnboardingContextProvider>
  );
};

export default React.memo(Onboarding);
